"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import API from "../services/api"
import "./SeminairePage.css"
import CompressedFileInput from "../components/CompressedFileInput"

const SeminairesPage = () => {
  const navigate = useNavigate()
  const [seminaires, setSeminaires] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    nom: "",
    surface: "",
    hauteur: "",
    theatre: 0,
    salle_u: 0,
    salle_reunion: 0,
    salle_classe: 0,
    banquet: 0,
    images: [],
    fiche: null,
    translations: {
      fr: { nom: "" },
      en: { nom: "" },
      ar: { nom: "" },
    },
  })
  const [existingImages, setExistingImages] = useState([])
  const [imagePreviews, setImagePreviews] = useState([])
  const [editId, setEditId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortOrder, setSortOrder] = useState("newest")
  const [viewMode, setViewMode] = useState("grid")
  const [user, setUser] = useState({ username: "", email: "" })
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [currentLang, setCurrentLang] = useState("fr")

  // Theme state with localStorage initialization
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme")
    return savedTheme ? savedTheme === "dark" : true
  })

  const fetchSeminaires = async () => {
    setIsLoading(true)
    try {
      const res = await API.get("/seminaires")
      setSeminaires(res.data)
    } catch (error) {
      console.error("Erreur lors du chargement des séminaires:", error)
      alert("Impossible de charger les séminaires")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const token = localStorage.getItem("accessToken")
    if (!token) navigate("/")

    const userData = JSON.parse(localStorage.getItem("user") || '{"username":"test","email":"test@gmail.com"}')
    setUser(userData)

    const loadTimer = setTimeout(() => setIsLoading(false), 800)
    const timer = setInterval(() => setCurrentTime(new Date()), 60000)

    fetchSeminaires()

    return () => {
      clearTimeout(loadTimer)
      clearInterval(timer)
    }
  }, [navigate])

  useEffect(() => {
    localStorage.setItem("theme", isDarkMode ? "dark" : "light")
  }, [isDarkMode])

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode)
  }

  const handleAddImage = (e) => {
    const files = e.target.files
    if (files && files[0]) {
      const file = files[0]
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, file],
      }))

      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result])
      }
      reader.readAsDataURL(file)
    }
    // Reset input
    e.target.value = ""
  }

  const handleRemoveImage = (index) => {
    // Check if this is an existing image or a new one
    const existingImageCount = existingImages.length

    if (index < existingImageCount) {
      // Removing an existing image
      setExistingImages((prev) => prev.filter((_, i) => i !== index))
    } else {
      // Removing a new image
      const newImageIndex = index - existingImageCount
      setFormData((prev) => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== newImageIndex),
      }))
    }

    setImagePreviews((prev) => prev.filter((_, i) => i !== index))
  }

  const handleChange = (e) => {
    const { name, value, files } = e.target

    if (name === "fiche" && files && files[0]) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }))
      return
    }

    if (name === "nom") {
      setFormData((prev) => {
        const safeTranslations = prev.translations || {
          fr: { nom: "" },
          en: { nom: "" },
          ar: { nom: "" },
        }

        const updatedTranslations = {
          ...safeTranslations,
          [currentLang]: {
            ...(safeTranslations[currentLang] || { nom: "" }),
            nom: value,
          },
        }

        return {
          ...prev,
          nom: currentLang === "fr" ? value : prev.nom,
          translations: updatedTranslations,
        }
      })
      return
    }

    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const form = new FormData()

      const translationsData = formData.translations || {
        fr: { nom: formData.nom || "" },
        en: { nom: "" },
        ar: { nom: "" },
      }

      // Add text fields
      form.append("nom", translationsData.fr?.nom || formData.nom || "")
      form.append("surface", formData.surface)
      form.append("hauteur", formData.hauteur)
      form.append("theatre", formData.theatre)
      form.append("salle_u", formData.salle_u)
      form.append("salle_reunion", formData.salle_reunion)
      form.append("salle_classe", formData.salle_classe)
      form.append("banquet", formData.banquet)
      form.append("translations", JSON.stringify(translationsData || {}))

      // Add new image files
      formData.images.forEach((image) => {
        form.append("images", image)
      })

      // Add existing images that should be kept (as JSON array)
      form.append("existingImages", JSON.stringify(existingImages))

      // Add PDF fiche if present
      if (formData.fiche) {
        form.append("fiche", formData.fiche)
      }

      if (editId) {
        await API.put(`/seminaires/${editId}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        alert("Séminaire modifié avec succès")
      } else {
        await API.post("/seminaires", form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        alert("Séminaire créé avec succès")
      }

      resetForm()
      fetchSeminaires()
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error)
      alert(editId ? "Échec de la modification" : "Échec de la création")
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      nom: "",
      surface: "",
      hauteur: "",
      theatre: 0,
      salle_u: 0,
      salle_reunion: 0,
      salle_classe: 0,
      banquet: 0,
      images: [],
      fiche: null,
      translations: {
        fr: { nom: "" },
        en: { nom: "" },
        ar: { nom: "" },
      },
    })
    setExistingImages([])
    setImagePreviews([])
    setEditId(null)
    setShowForm(false)
    setIsLoading(false)
  }

  const handleEdit = (seminaire) => {
    const existingTranslations = seminaire.translations || {
      fr: { nom: seminaire.nom || "" },
      en: { nom: "" },
      ar: { nom: "" },
    }

    setFormData({
      nom: existingTranslations.fr?.nom || seminaire.nom || "",
      surface: seminaire.surface,
      hauteur: seminaire.hauteur,
      theatre: seminaire.theatre || 0,
      salle_u: seminaire.salle_u || 0,
      salle_reunion: seminaire.salle_reunion || 0,
      salle_classe: seminaire.salle_classe || 0,
      banquet: seminaire.banquet || 0,
      images: [],
      fiche: null,
      translations: {
        fr: { nom: existingTranslations.fr?.nom || seminaire.nom || "" },
        en: { nom: existingTranslations.en?.nom || "" },
        ar: { nom: existingTranslations.ar?.nom || "" },
      },
    })
    setEditId(seminaire._id)
    setShowForm(true)
    setCurrentLang("fr")

    if (seminaire.images && seminaire.images.length > 0) {
      setExistingImages(seminaire.images)
      setImagePreviews(seminaire.images)
    } else {
      setExistingImages([])
      setImagePreviews([])
    }
  }

  const handleDelete = async (id) => {
    try {
      if (window.confirm("Êtes-vous sûr de vouloir supprimer ce séminaire ?")) {
        setIsLoading(true)
        await API.delete(`/seminaires/${id}`)
        alert("Séminaire supprimé avec succès")
        fetchSeminaires()
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error)
      alert("Échec de la suppression")
      setIsLoading(false)
    }
  }

  const formatDate = (date) => {
    const options = { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    return date.toLocaleDateString("fr-FR", options)
  }

  const formatTime = (date) => {
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const filteredSeminaires = seminaires
    .filter((s) => s.nom.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortOrder === "newest") {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      } else if (sortOrder === "oldest") {
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      } else if (sortOrder === "alphabetical") {
        return a.nom.localeCompare(b.nom)
      }
      return 0
    })

  if (isLoading) {
    return (
      <div className={isDarkMode ? "loading-container" : "light-loading-container"}>
        <div className={isDarkMode ? "loading-logo" : "light-loading-logo"}>
          <img
            src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
            alt="Guestly Logo"
            className="logo-image"
          />
        </div>
        <div className={isDarkMode ? "loading-spinner" : "light-loading-spinner"}>
          <div className={isDarkMode ? "spinner-circle" : "light-spinner-circle"}></div>
          <div className={isDarkMode ? "spinner-circle-inner" : "light-spinner-circle-inner"}></div>
        </div>
        <div className={isDarkMode ? "loading-text" : "light-loading-text"}>Chargement...</div>
      </div>
    )
  }

  return (
    <div className={isDarkMode ? "dashboard" : "light-dashboard"}>
      <div className={isDarkMode ? "mobile-header" : "light-mobile-header"}>
        <button className={isDarkMode ? "menu-toggle" : "light-menu-toggle"} onClick={toggleSidebar}>
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className={isDarkMode ? "mobile-logo" : "light-mobile-logo"}>
          <span style={{ display: "inline-block", verticalAlign: "middle" }}>
            <img
              src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
              alt="Guestly Logo"
              style={{
                width: "180px",
                height: "auto",
                objectFit: "contain",
                transition: "opacity 0.3s ease",
              }}
            />
          </span>
        </div>
        <div className={isDarkMode ? "mobile-user" : "light-mobile-user"}>
          <div className={isDarkMode ? "user-avatar" : "light-user-avatar"}>{user.username.charAt(0)}</div>
        </div>
      </div>

      <div className={`${isDarkMode ? "sidebar" : "light-sidebar"} ${isSidebarOpen ? "open" : ""}`}>
        <div className={isDarkMode ? "sidebar-header" : "light-sidebar-header"}>
          <div className={isDarkMode ? "logo" : "light-logo"}>
            <span style={{ display: "inline-block", verticalAlign: "middle" }}>
              <img
                src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
                alt="Guestly Logo"
                style={{
                  width: "180px",
                  height: "auto",
                  objectFit: "contain",
                  transition: "opacity 0.3s ease",
                }}
              />
            </span>
          </div>
          <button className={isDarkMode ? "close-sidebar" : "light-close-sidebar"} onClick={toggleSidebar}>
            ×
          </button>
        </div>

        <div className={isDarkMode ? "user-profile" : "light-user-profile"}>
          <div className={isDarkMode ? "user-avatar" : "light-user-avatar"}>{user.username.charAt(0)}</div>
          <div className={isDarkMode ? "user-info" : "light-user-info"}>
            <div className={isDarkMode ? "user-name" : "light-user-name"}>{user.username}</div>
            <div className={isDarkMode ? "user-email" : "light-user-email"}>{user.email}</div>
          </div>
        </div>

        <div className={isDarkMode ? "sidebar-date" : "light-sidebar-date"}>
          <div className={isDarkMode ? "date-display" : "light-date-display"}>{formatDate(currentTime)}</div>
          <div className={isDarkMode ? "time-display" : "light-time-display"}>{formatTime(currentTime)}</div>
        </div>

        <nav className={isDarkMode ? "sidebar-nav" : "light-sidebar-nav"}>
          <ul>
            <li>
              <a href="/dashboard">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🏠</span>
                <span>Tableau de bord</span>
              </a>
            </li>
            <li>
              <a href="/profile">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>👤</span>
                <span>Mon profil</span>
              </a>
            </li>
            <li>
              <a href="/settings">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>⚙️</span>
                <span>Paramètres</span>
              </a>
            </li>
            <li className="active">
              <a href="/seminaires">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🏛️</span>
                <span>Business</span>
              </a>
            </li>
          </ul>
        </nav>

        <div className={isDarkMode ? "sidebar-footer" : "light-sidebar-footer"}>
          <button
            className={isDarkMode ? "logout-button" : "light-logout-button"}
            onClick={() => {
              localStorage.removeItem("accessToken")
              localStorage.removeItem("user")
              navigate("/")
            }}
          >
            <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🚪</span>
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      <div className={isDarkMode ? "main-content" : "light-main-content"}>
        <div className={isDarkMode ? "welcome-section" : "light-welcome-section"}>
          <div className={isDarkMode ? "welcome-header" : "light-welcome-header"}>
            <h1>
              Business <span className={isDarkMode ? "wave-emoji" : "light-wave-emoji"}>🏛️</span>
            </h1>
            <div className={isDarkMode ? "welcome-actions" : "light-welcome-actions"}>
              <button
                className={isDarkMode ? "theme-toggle-button" : "light-theme-toggle-button"}
                onClick={toggleTheme}
                title={isDarkMode ? "Passer en mode clair" : "Passer en mode sombre"}
              >
                {isDarkMode ? "☀️" : "🌙"}
              </button>

              <div className={isDarkMode ? "user-avatar-small" : "light-user-avatar-small"}>
                {user.username.charAt(0)}
              </div>
            </div>
          </div>
          <p>Gérez vos espaces de séminaires et leurs caractéristiques</p>
        </div>

        <div className={isDarkMode ? "search-filter-container" : "light-search-filter-container"}>
          <div className={isDarkMode ? "search-container" : "light-search-container"}>
            <span className="search-icon">🔍</span>
            <input
              className={isDarkMode ? "search-input" : "light-search-input"}
              type="text"
              placeholder="Rechercher un séminaire..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="view-sort-container">
            <div className={isDarkMode ? "view-toggle" : "light-view-toggle"}>
              <button
                className={`${isDarkMode ? "view-btn" : "light-view-btn"} ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label="Vue en grille"
              >
                ▦
              </button>
              <button
                className={`${isDarkMode ? "view-btn" : "light-view-btn"} ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label="Vue en liste"
              >
                ☰
              </button>
            </div>

            <select
              className={isDarkMode ? "sort-select" : "light-sort-select"}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="newest">Plus récent</option>
              <option value="oldest">Plus ancien</option>
              <option value="alphabetical">Alphabétique</option>
            </select>
          </div>
        </div>

        <div className={isDarkMode ? "section-header" : "light-section-header"}>
          <h2>Liste des Séminaires</h2>
          <div className="section-actions">
            <button
              className={isDarkMode ? "section-action" : "light-section-action"}
              onClick={() => setShowForm(!showForm)}
            >
              <span>{showForm ? "Annuler" : "Ajouter"}</span>
              <span className="action-icon">{showForm ? "❌" : "+"}</span>
            </button>
          </div>
        </div>

        {showForm && (
          <div className={isDarkMode ? "form-container" : "light-form-container"}>
            <form onSubmit={handleSubmit} className={isDarkMode ? "seminaire-form" : "light-seminaire-form"}>
              <div className="language-toggle">
                {["fr", "ar", "en"].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    className={`lang-btn ${currentLang === lang ? "active" : ""} ${
                      isDarkMode ? "" : "light"
                    }`}
                    onClick={() => setCurrentLang(lang)}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
              <input
                type="text"
                name="nom"
                placeholder="Nom du séminaire"
                value={formData.translations?.[currentLang]?.nom || ""}
                onChange={handleChange}
                required
                className={isDarkMode ? "" : "light-form-input"}
              />
              <input
                type="number"
                name="surface"
                placeholder="Surface en m²"
                value={formData.surface}
                onChange={handleChange}
                required
                className={isDarkMode ? "" : "light-form-input"}
              />
              <input
                type="number"
                name="hauteur"
                placeholder="Hauteur en m"
                value={formData.hauteur}
                onChange={handleChange}
                required
                className={isDarkMode ? "" : "light-form-input"}
              />

              <div className="capacity-grid">
                <div className="capacity-field-group">
                  <label className="capacity-label">Théâtre</label>
                  <p className="capacity-description">(personnes)</p>
                  <input
                    type="number"
                    name="theatre"
                    placeholder="Nombre de personnes"
                    value={formData.theatre}
                    onChange={handleChange}
                    min="0"
                    className={isDarkMode ? "" : "light-form-input"}
                  />
                </div>
                <div className="capacity-field-group">
                  <label className="capacity-label">Salle U</label>
                  <p className="capacity-description">(personnes)</p>
                  <input
                    type="number"
                    name="salle_u"
                    placeholder="Nombre de personnes"
                    value={formData.salle_u}
                    onChange={handleChange}
                    min="0"
                    className={isDarkMode ? "" : "light-form-input"}
                  />
                </div>
                <div className="capacity-field-group">
                  <label className="capacity-label">Salle Réunion</label>
                  <p className="capacity-description">(personnes)</p>
                  <input
                    type="number"
                    name="salle_reunion"
                    placeholder="Nombre de personnes"
                    value={formData.salle_reunion}
                    onChange={handleChange}
                    min="0"
                    className={isDarkMode ? "" : "light-form-input"}
                  />
                </div>
                <div className="capacity-field-group">
                  <label className="capacity-label">Salle Classe</label>
                  <p className="capacity-description">(personnes)</p>
                  <input
                    type="number"
                    name="salle_classe"
                    placeholder="Nombre de personnes"
                    value={formData.salle_classe}
                    onChange={handleChange}
                    min="0"
                    className={isDarkMode ? "" : "light-form-input"}
                  />
                </div>
                <div className="capacity-field-group">
                  <label className="capacity-label">Banquet</label>
                  <p className="capacity-description">(personnes)</p>
                  <input
                    type="number"
                    name="banquet"
                    placeholder="Nombre de personnes"
                    value={formData.banquet}
                    onChange={handleChange}
                    min="0"
                    className={isDarkMode ? "" : "light-form-input"}
                  />
                </div>
              </div>

              <div className="image-management-section">
                <label className="form-label">Images du séminaire</label>
                <div className="image-previews-container">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={preview || "/placeholder.svg"} alt={`Aperçu ${index + 1}`} className="image-preview" />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => handleRemoveImage(index)}
                        title="Supprimer cette image"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <label className="add-image-label">
                  <CompressedFileInput
                    type="file"
                    name="images"
                    accept="image/*"
                    onChange={handleAddImage}
                    className="hidden-file-input"
                  />
                  <span className="add-image-btn">+ Ajouter une image</span>
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">Fiche PDF</label>
                <input
                  type="file"
                  name="fiche"
                  accept="application/pdf"
                  onChange={handleChange}
                  className={isDarkMode ? "" : "light-form-input"}
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className={isDarkMode ? "btn btn-primary" : "light-btn light-btn-primary"}
                  disabled={isLoading}
                >
                  {isLoading ? "Chargement..." : editId ? "✅ Modifier" : "✅ Créer"}
                </button>
                <button
                  type="button"
                  className={isDarkMode ? "btn btn-secondary" : "light-btn light-btn-secondary"}
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  ❌ Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {filteredSeminaires.length === 0 ? (
          <div className={isDarkMode ? "empty-state" : "light-empty-state"}>
            <div className="empty-icon">🏛️</div>
            <h3>Aucun séminaire trouvé</h3>
            <p>Commencez par créer un nouveau séminaire</p>
            <button
              className={isDarkMode ? "btn btn-primary" : "light-btn light-btn-primary"}
              onClick={() => setShowForm(true)}
            >
              Créer un séminaire
            </button>
          </div>
        ) : (
          <div
            className={`${isDarkMode ? "seminaires-list" : "light-seminaires-list"} ${viewMode === "list" ? "list-view" : "grid"}`}
          >
            {filteredSeminaires.map((seminaire) => (
              <div key={seminaire._id} className={isDarkMode ? "seminaire-card" : "light-seminaire-card"}>
                {seminaire.images && seminaire.images.length > 0 && (
                  <img
                    src={seminaire.images[0] || "/placeholder.svg"}
                    alt={seminaire.nom}
                    className="card-image"
                    onError={(e) => (e.target.src = "/placeholder.svg")}
                  />
                )}
                <h2>{seminaire.nom}</h2>
                <div className="card-details">
                  <div className="detail-item">
                    <span className="detail-label">Surface:</span>
                    <span className="detail-value">{seminaire.surface} m²</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Hauteur:</span>
                    <span className="detail-value">{seminaire.hauteur} m</span>
                  </div>
                  {seminaire.theatre > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Théâtre:</span>
                      <span className="detail-value">{seminaire.theatre} pers.</span>
                    </div>
                  )}
                  {seminaire.salle_u > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Salle U:</span>
                      <span className="detail-value">{seminaire.salle_u} pers.</span>
                    </div>
                  )}
                  {seminaire.salle_reunion > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Réunion:</span>
                      <span className="detail-value">{seminaire.salle_reunion} pers.</span>
                    </div>
                  )}
                  {seminaire.salle_classe > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Classe:</span>
                      <span className="detail-value">{seminaire.salle_classe} pers.</span>
                    </div>
                  )}
                  {seminaire.banquet > 0 && (
                    <div className="detail-item">
                      <span className="detail-label">Banquet:</span>
                      <span className="detail-value">{seminaire.banquet} pers.</span>
                    </div>
                  )}
                </div>
                <div className="seminaire-card-actions">
                  <button
                    onClick={() => handleEdit(seminaire)}
                    className={isDarkMode ? "" : "light-btn light-btn-secondary"}
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(seminaire._id)}
                    className={isDarkMode ? "" : "light-btn light-btn-danger"}
                  >
                    🗑️ Supprimer
                  </button>
                  {seminaire.fiche && (
                    <a
                      href={seminaire.fiche}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={isDarkMode ? "btn btn-secondary" : "light-btn light-btn-secondary"}
                      style={{ textDecoration: "none" }}
                    >
                      📄 Voir la fiche
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .language-toggle {
          display: inline-flex;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .lang-btn {
          padding: 0.35rem 0.75rem;
          border-radius: 999px;
          border: 1px solid #4b5563;
          background: transparent;
          color: #e5e7eb;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .lang-btn.light {
          border-color: #e2e8f0;
          color: #1f2933;
        }

        .lang-btn.active {
          background: linear-gradient(45deg, #ec4899, #8b5cf6);
          border-color: transparent;
          color: #ffffff;
          box-shadow: 0 2px 6px rgba(236, 72, 153, 0.35);
        }

        .lang-btn:not(.active):hover {
          background: rgba(148, 163, 184, 0.2);
        }

        .lang-btn.light:not(.active):hover {
          background: rgba(226, 232, 240, 0.75);
        }

        .capacity-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }

        .capacity-field-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .capacity-label {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 14px;
        }

        .capacity-description {
          font-size: 12px;
          color: var(--text-medium);
          margin: 0;
          font-style: italic;
        }

        .image-management-section {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: var(--text-medium);
        }

        .image-previews-container {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
          min-height: 40px;
        }

        .image-preview-item {
          position: relative;
          display: inline-block;
        }

        .image-preview {
          width: 120px;
          height: 120px;
          object-fit: cover;
          border-radius: var(--radius-md);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: var(--shadow-sm);
        }

        .remove-image-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--accent-red);
          color: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 16px;
          font-weight: bold;
          box-shadow: var(--shadow-sm);
          transition: all 0.2s ease;
        }

        .remove-image-btn:hover {
          background: #d32f2f;
          transform: scale(1.1);
        }

        .hidden-file-input {
          display: none;
        }

        .add-image-label {
          display: inline-block;
          cursor: pointer;
        }

        .add-image-btn {
          display: inline-block;
          padding: 10px 16px;
          background: rgba(236, 64, 122, 0.2);
          border: 2px dashed var(--primary);
          border-radius: var(--radius-md);
          color: var(--primary);
          font-weight: 500;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .add-image-btn:hover {
          background: rgba(236, 64, 122, 0.3);
          transform: translateY(-2px);
        }

        .form-group {
          margin-bottom: 20px;
        }

        /* Light mode image styles */
        .light-image-preview {
          border: 1px solid #e2e8f0;
        }

        .light-add-image-btn {
          border-color: #ec4899;
          color: #ec4899;
          background: rgba(236, 72, 153, 0.1);
        }

        .light-add-image-btn:hover {
          background: rgba(236, 72, 153, 0.15);
        }
      `}</style>
    </div>
  )
}

export default SeminairesPage
